import React from 'react';
import { PageHeader } from '@dashboard/components/PageHeader';
import { Skeleton } from '@dashboard/components/ui/skeleton';
import LinkInBioTabs, { useLinkInBioTab } from './LinkInBioTabs';

function SectionSkeleton({ action, children }: { action?: boolean; children: React.ReactNode }) {
  return (
    <div className="space-y-4 border-t pt-6">
      <div className="flex items-start justify-between gap-4">
        <div className="space-y-0.5">
          <div className="flex h-6 items-center">
            <Skeleton className="h-4 w-24" />
          </div>
          <div className="flex h-5 items-center">
            <Skeleton className="h-3.5 w-56" />
          </div>
        </div>
        {action && <Skeleton className="h-7 w-24" />}
      </div>
      {children}
    </div>
  );
}

function BlockRows() {
  return (
    <div className="divide-y rounded-lg border px-3">
      {Array.from({ length: 4 }, (_, index) => (
        <div key={index} className="flex items-center gap-3 py-2.5">
          <Skeleton className="size-4" />
          <Skeleton className="size-4" />
          <div className="flex-1">
            <div className="flex h-5 items-center">
              <Skeleton className="h-3.5 w-40" />
            </div>
            <div className="flex h-4 items-center">
              <Skeleton className="h-3 w-24" />
            </div>
          </div>
          <Skeleton className="h-5 w-9 rounded-full" />
          <Skeleton className="size-4" />
        </div>
      ))}
    </div>
  );
}

function FieldRows() {
  return (
    <>
      {Array.from({ length: 3 }, (_, index) => (
        <div key={index} className="space-y-1.5">
          <Skeleton className="h-4 w-28" />
          <Skeleton className="h-9 w-full" />
        </div>
      ))}
    </>
  );
}

export default function LinkInBioSkeleton() {
  const tab = useLinkInBioTab();

  return (
    <div className="space-y-4 p-4" role="status" aria-label="Loading your link in bio">
      <PageHeader
        title="Link in bio"
        description={
          <span className="flex h-7 items-center">
            <span className="h-4 w-64 animate-pulse rounded-md bg-muted" />
          </span>
        }
        topBarActions={
          <>
            <Skeleton className="hidden h-5 w-10 sm:block" />
            <Skeleton className="h-8 w-20" />
            <Skeleton className="h-8 w-24" />
            <Skeleton className="h-8 w-32" />
          </>
        }
      />
      <div className="grid gap-6 lg:grid-cols-12">
        <div className="min-w-0 space-y-6 lg:col-span-7">
          <LinkInBioTabs />
          {tab === 'blocks' ? (
            <SectionSkeleton action>
              <BlockRows />
            </SectionSkeleton>
          ) : (
            <SectionSkeleton>
              <FieldRows />
            </SectionSkeleton>
          )}
        </div>
        <div className="hidden lg:col-span-5 lg:block">
          <div className="sticky top-4 flex flex-col gap-3 rounded-xl border border-gray-100 bg-neutralgray p-3 dark:border-border dark:bg-background">
            <div className="flex h-8 items-center justify-between">
              <p className="text-sm font-medium">Preview</p>
              <Skeleton className="h-7 w-16" />
            </div>
            <Skeleton
              className="mx-auto aspect-[9/19.5] max-w-full rounded-[2.75rem]"
              style={{ height: 'min(760px, calc(100vh - 9.5rem))' }}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
